/**
 * Admin Memberships Tab (Positions Monitor)
 *
 * View and manage per-position membership ledger.
 * Admin-only. Gate: ADMIN_MEMBERSHIP_UI_ENABLED.
 */

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import {
  listPositions,
  getMembershipDetails,
  extendTtl,
  cleanup,
  forceExitSubscriber,
  rebuildMembership,
  type MembershipListResponse,
  type MembershipDetailResponse,
} from "@app/services/adminMembershipsApi";
import "./admin-memberships.css";

const LIST_QUERY_KEY = ["admin", "memberships", "list"];

export function AdminMembershipsTab(): JSX.Element {
  const queryClient = useQueryClient();
  const [stateFilter, setStateFilter] = useState<"open" | "recent" | undefined>(undefined);
  const [selectedPositionId, setSelectedPositionId] = useState<string | null>(null);
  const [extendDays, setExtendDays] = useState(7);
  const [forceExitUserId, setForceExitUserId] = useState("");
  const [forceExitOverride, setForceExitOverride] = useState(false);
  const [forceExitReason, setForceExitReason] = useState("admin_force_exit");

  const { data: listData, isLoading: listLoading, isError: listError } = useQuery<MembershipListResponse>({
    queryKey: [LIST_QUERY_KEY, stateFilter],
    queryFn: () => listPositions({ state: stateFilter, limit: 100 }),
    refetchInterval: 30000,
  });

  const { data: detailData, isLoading: detailLoading } = useQuery<MembershipDetailResponse>({
    queryKey: ["admin", "memberships", "detail", selectedPositionId],
    queryFn: () => getMembershipDetails(selectedPositionId!),
    enabled: !!selectedPositionId,
  });

  const extendMutation = useMutation({
    mutationFn: ({ id, days }: { id: string; days: number }) =>
      extendTtl(id, { days, reason: "admin_extend" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LIST_QUERY_KEY });
      if (selectedPositionId) {
        queryClient.invalidateQueries({ queryKey: ["admin", "memberships", "detail", selectedPositionId] });
      }
    },
  });

  const cleanupDryRunMutation = useMutation({
    mutationFn: (id: string) => cleanup(id, { dry_run: true }),
  });

  const cleanupDeleteMutation = useMutation({
    mutationFn: (id: string) => cleanup(id, { dry_run: false, delete: true, reason: "admin_cleanup" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LIST_QUERY_KEY });
      setSelectedPositionId(null);
    },
  });

  const forceExitMutation = useMutation({
    mutationFn: ({ id, userId, reason, override }: { id: string; userId: string; reason: string; override: boolean }) =>
      forceExitSubscriber(id, {
        user_id: userId,
        reason,
        override_membership_check: override,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LIST_QUERY_KEY });
      if (selectedPositionId) {
        queryClient.invalidateQueries({ queryKey: ["admin", "memberships", "detail", selectedPositionId] });
      }
    },
  });

  const rebuildDryRunMutation = useMutation({
    mutationFn: (id: string) => rebuildMembership(id, { dry_run: true }),
  });

  const rebuildApplyMutation = useMutation({
    mutationFn: (id: string) => rebuildMembership(id, { dry_run: false, reason: "admin_rebuild" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: LIST_QUERY_KEY });
      if (selectedPositionId) {
        queryClient.invalidateQueries({ queryKey: ["admin", "memberships", "detail", selectedPositionId] });
      }
    },
  });

  if (listLoading) {
    return (
      <div className="admin-memberships-root">
        <Card title="Positions Monitor">
          <div className="admin-memberships-loading">Loading membership ledger...</div>
        </Card>
      </div>
    );
  }

  if (listError) {
    return (
      <div className="admin-memberships-root">
        <Card title="Positions Monitor">
          <div className="admin-memberships-error">
            Failed to load membership ledger. Check ADMIN_MEMBERSHIP_UI_ENABLED.
          </div>
        </Card>
      </div>
    );
  }

  const positions = listData?.positions ?? [];

  return (
    <div className="admin-memberships-root">
      <Card title="Positions Monitor" subtitle="Per-position membership ledger">
        <div className="admin-memberships-filters">
          <label>
            State:{" "}
            <select
              value={stateFilter ?? ""}
              onChange={(e) => setStateFilter((e.target.value || undefined) as "open" | "recent" | undefined)}
            >
              <option value="">All</option>
              <option value="open">Open</option>
              <option value="recent">Recent (Closed)</option>
            </select>
          </label>
        </div>

        <div className="admin-memberships-grid">
          <div className="admin-memberships-list">
            <h3>Positions ({positions.length})</h3>
            <div className="admin-memberships-table-wrap">
              <table className="admin-memberships-table">
                <thead>
                  <tr>
                    <th>Position ID</th>
                    <th>Symbol</th>
                    <th>State</th>
                    <th>Members</th>
                    <th>TTL</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.map((p) => (
                    <tr
                      key={p.admin_position_id}
                      className={selectedPositionId === p.admin_position_id ? "selected" : ""}
                      onClick={() => setSelectedPositionId(p.admin_position_id)}
                    >
                      <td className="admin-memberships-cell-mono">{p.admin_position_id}</td>
                      <td>{p.symbol}</td>
                      <td>
                        <span className={`admin-memberships-badge admin-memberships-badge--${p.state.toLowerCase()}`}>
                          {p.state}
                        </span>
                      </td>
                      <td>{p.members_count}</td>
                      <td>{p.ttl_seconds != null ? `${Math.round(p.ttl_seconds / 3600)}h` : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {positions.length === 0 && <p className="admin-memberships-empty">No positions found.</p>}
            </div>
          </div>

          <div className="admin-memberships-detail">
            {!selectedPositionId ? (
              <p className="admin-memberships-hint">Select a position to view details and actions.</p>
            ) : detailLoading ? (
              <div className="admin-memberships-loading">Loading details...</div>
            ) : detailData ? (
              <>
                <h3>{detailData.admin_position_id}</h3>
                <div className="admin-memberships-meta">
                  <pre>{JSON.stringify(detailData.meta, null, 2)}</pre>
                </div>
                <div className="admin-memberships-members">
                  <h4>Members ({detailData.members.length})</h4>
                  {detailData.derived_stuck_users && detailData.derived_stuck_users.length > 0 && (
                    <p className="admin-memberships-resolved-label">
                      Resolved from DB (emails shown when available)
                    </p>
                  )}
                  <ul>
                    {detailData.derived_stuck_users && detailData.derived_stuck_users.length > 0
                      ? detailData.derived_stuck_users.map((entry: Record<string, unknown>) => (
                          <li key={String(entry.user_id || "")}>
                            <div className="admin-memberships-member-info">
                              <span className="admin-memberships-member-email">
                                {String(entry.email || entry.user_id || "Unknown")}
                              </span>
                              <span className="admin-memberships-member-id">
                                ID: {String(entry.user_id || "").slice(0, 12)}...
                              </span>
                            </div>
                            <Button
                              size="small"
                              variant="neutral"
                              onClick={() => {
                                setForceExitUserId(String(entry.user_id || ""));
                                forceExitMutation.mutate({
                                  id: selectedPositionId,
                                  userId: String(entry.user_id || ""),
                                  reason: forceExitReason,
                                  override: forceExitOverride,
                                });
                              }}
                              disabled={forceExitMutation.isPending}
                            >
                              Force Exit
                            </Button>
                          </li>
                        ))
                      : detailData.members.map((uid) => (
                          <li key={uid}>
                            <span className="admin-memberships-cell-mono">{uid}</span>
                            <Button
                              size="small"
                              variant="neutral"
                              onClick={() => {
                                setForceExitUserId(uid);
                                forceExitMutation.mutate({
                                  id: selectedPositionId,
                                  userId: uid,
                                  reason: forceExitReason,
                                  override: forceExitOverride,
                                });
                              }}
                              disabled={forceExitMutation.isPending}
                            >
                              Force Exit
                            </Button>
                          </li>
                        ))}
                  </ul>
                </div>

                <div className="admin-memberships-actions">
                  <h4>Actions</h4>
                  <div className="admin-memberships-action-row">
                    <span>Extend TTL:</span>
                    <input
                      type="number"
                      min={1}
                      max={90}
                      value={extendDays}
                      onChange={(e) => setExtendDays(Number(e.target.value))}
                    />
                    <span>days</span>
                    <Button
                      size="small"
                      onClick={() => extendMutation.mutate({ id: selectedPositionId, days: extendDays })}
                      disabled={extendMutation.isPending}
                    >
                      Extend
                    </Button>
                  </div>
                  <div className="admin-memberships-action-row">
                    <Button
                      size="small"
                      variant="neutral"
                      onClick={() => cleanupDryRunMutation.mutate(selectedPositionId)}
                      disabled={cleanupDryRunMutation.isPending}
                    >
                      Cleanup (dry run)
                    </Button>
                    {cleanupDryRunMutation.data && (
                      <span className="admin-memberships-result">{cleanupDryRunMutation.data.message}</span>
                    )}
                  </div>
                  <div className="admin-memberships-action-row">
                    <Button
                      size="small"
                      variant="danger"
                      onClick={() => {
                        if (confirm("Delete keys? Only allowed when SCARD=0 and state=CLOSED.")) {
                          cleanupDeleteMutation.mutate(selectedPositionId);
                        }
                      }}
                      disabled={cleanupDeleteMutation.isPending}
                    >
                      Cleanup (delete)
                    </Button>
                  </div>
                  <div className="admin-memberships-action-row">
                    <Button
                      size="small"
                      variant="neutral"
                      onClick={() => rebuildDryRunMutation.mutate(selectedPositionId)}
                      disabled={rebuildDryRunMutation.isPending}
                    >
                      Rebuild (dry run)
                    </Button>
                    {rebuildDryRunMutation.data && (
                      <span className="admin-memberships-result">
                        {rebuildDryRunMutation.data.message}
                      </span>
                    )}
                  </div>
                  <div className="admin-memberships-action-row">
                    <Button
                      size="small"
                      onClick={() => {
                        if (confirm("Rebuild membership from hybrid sources?")) {
                          rebuildApplyMutation.mutate(selectedPositionId);
                        }
                      }}
                      disabled={rebuildApplyMutation.isPending}
                    >
                      Rebuild (apply)
                    </Button>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </Card>
    </div>
  );
}
